import * as React from 'react';
import { InputHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

import { Colors } from '../../core/styles';

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
    size: number;
    round: boolean;
    color: string;
    checkmarkColor: string;
    borderColor: string;
    emptyColor: string;
    checked: boolean;
    disabled?: boolean;
    'data-role'?: string;
}

function sizePercent(percent: number): (props: CheckboxProps) => number {
    return (props: CheckboxProps) => props.size * (percent / 100);
}

const StyledCheckbox = styled.div<CheckboxProps>`
    position: relative;
    user-select: none;
    width: ${sizePercent(100)}px;
    height: ${sizePercent(100)}px;

    input {
        position: absolute;
        opacity: 0;
        height: 0;
        width: 0;
    }

    input[type='checkbox']:focus-visible + span {
        outline: 2px solid Highlight;
        outline-offset: 2px;
    }

    span {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 5px;
        border: solid 1px ${(props) => props.borderColor};
        background-color: ${(props) => props.emptyColor};
        transition: background-color 50ms;

        ${(props) =>
            props.round &&
            css`
                border-radius: 50%;
            `}

        &:hover {
            border-color: ${(props) => props.color};
        }

        &:after {
            content: '';
            position: absolute;
            display: none;
            left: ${sizePercent(36)}px;
            top: ${sizePercent(25)}px;
            width: ${sizePercent(20)}px;
            height: ${sizePercent(40)}px;
            border: solid ${(props) => props.checkmarkColor};
            border-width: 0 ${sizePercent(9)}px ${sizePercent(9)}px 0;
            transform: rotate(45deg);
        }

        ${({ checked, color }) =>
            checked &&
            css`
                background-color: ${color};
                border-color: ${color};

                &:after {
                    display: block;
                }
            `}
    }
`;

export class Checkbox extends React.Component<CheckboxProps> {
    public static defaultProps = {
        size: 25,
        round: false,
        color: Colors.blue,
        checkmarkColor: Colors.white,
        emptyColor: Colors.creamy,
        borderColor: '#e4e4e4'
    };

    public render(): React.JSX.Element {
        const {
            size,
            round,
            color,
            checkmarkColor,
            emptyColor,
            borderColor,
            checked,
            disabled,
            'data-role': dataRole,
            ...rest
        } = this.props;
        const stylingProps = {
            size,
            round,
            color: disabled ? Colors.disabledText : color,
            checkmarkColor,
            emptyColor,
            borderColor,
            checked
        };
        const ariaLabelId = dataRole ? `label-${dataRole.split('@')[0]}` : '';

        return (
            <StyledCheckbox {...stylingProps}>
                <input
                    type="checkbox"
                    {...rest}
                    checked={checked}
                    disabled={disabled}
                    data-role={dataRole}
                    id={dataRole}
                    aria-labelledby={ariaLabelId}
                />
                <span />
            </StyledCheckbox>
        );
    }
}
